import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { updatePasswordWithToken } from '../services/auth';

const getAccessTokenFromLocation = () => {
  // Supabase sends tokens in the hash fragment: #access_token=...&type=recovery
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const tokenFromHash = hashParams.get('access_token');

  // Fallback: if redirected with query params instead of hash
  const searchParams = new URLSearchParams(window.location.search);
  const tokenFromQuery = searchParams.get('access_token');

  return tokenFromHash || tokenFromQuery || '';
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const accessToken = useMemo(getAccessTokenFromLocation, [searchParams]);
  const isRecovery = searchParams.get('type') === 'recovery' || window.location.hash.includes('type=recovery');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!accessToken) {
      setError('Missing reset token. Please use the link from your email.');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await updatePasswordWithToken(accessToken, password);
      setSuccess('Password updated. You can now sign in with your new password.');
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      setError(err.message || 'Unable to update password right now');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      setError('Missing reset token. Open the link directly from your email.');
    }
  }, [accessToken]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#050815] via-[#0a0f1f] to-[#050815] px-4 text-slate-100">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0c1428]/90 p-8 shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">MyRolodex • Reset</p>
        <h1 className="mt-2 text-2xl font-semibold text-slate-50">Reset your password</h1>
        <p className="text-sm text-slate-400">
          {isRecovery
            ? 'Enter a new password to complete the reset.'
            : 'Follow the link from your email to reset your password.'}
        </p>

        {error && <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</div>}
        {success && (
          <div className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">
            {success}
          </div>
        )}

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span>New password</span>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              placeholder="Minimum 8 characters"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-base text-slate-100 outline-none transition focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/30"
            />
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-300">
            <span>Confirm password</span>
            <input
              type="password"
              name="confirm"
              autoComplete="new-password"
              placeholder="Repeat password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-base text-slate-100 outline-none transition focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/30"
            />
          </label>

          <button
            type="submit"
            className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-sky-500 px-4 py-3 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={loading}
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <p className="mt-4 text-sm text-slate-400">
          Remembered your password?{' '}
          <Link className="font-semibold text-sky-300 hover:underline" to="/login">
            Return to sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
